/**
 * PatternSelectionStep Component
 * Questionnaire to guide users to the right macro pattern
 */

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HelpCircle, CheckCircle2, ArrowRight, ChevronLeft, Lightbulb, Code } from 'lucide-react'
import { MacroPattern } from '../../../types/macros'

interface PatternSelectionStepProps {
  selectedPattern: MacroPattern | null
  onSelectPattern: (pattern: MacroPattern) => void
  onBack?: () => void
}

interface Question {
  id: string
  question: string
  options: {
    label: string
    value: string
    description: string
    pattern?: MacroPattern
    nextQuestion?: string
  }[]
}

const PatternSelectionStep: React.FC<PatternSelectionStepProps> = ({
  selectedPattern,
  onSelectPattern,
  onBack
}) => {
  const [currentQuestionId, setCurrentQuestionId] = useState<string>('q1')
  const [showAdvancedExamples, setShowAdvancedExamples] = useState(false)

  const questions: Record<string, Question> = {
    q1: {
      id: 'q1',
      question: 'How reliable is your data? Choose the scenario that matches your situation:',
      options: [
        {
          label: '✓ Data is always available',
          value: 'always',
          description: 'Use when data is guaranteed to exist (e.g., order IDs, confirmation numbers). Displays data directly without safety checks.',
          pattern: MacroPattern.SIMPLE
        },
        {
          label: '⚠️ Data might be missing → show a backup value',
          value: 'fallback',
          description: 'Perfect for optional fields like names or preferences. Shows your backup text when data is missing. Example: "Hello {{name|"valued customer"}}"',
          pattern: MacroPattern.FALLBACK
        },
        {
          label: '↔️ Data might be missing → adjust entire phrases',
          value: 'conditional',
          description: 'Best for maintaining natural grammar when data may not exist. Either shows complete phrases or hides them entirely to keep messages flowing naturally.',
          nextQuestion: 'q2'
        },
        {
          label: '🔧 Multiple scenarios in one message',
          value: 'advanced',
          description: 'For complex messages that need several different patterns working together. Combine replacements, fallbacks, and conditionals for sophisticated personalization.',
          pattern: MacroPattern.ADVANCED
        }
      ]
    },
    q2: {
      id: 'q2',
      question: 'When data is missing, what should happen to keep your message natural?',
      options: [
        {
          label: 'Hide the entire phrase gracefully',
          value: 'hide',
          description: 'The phrase disappears completely, maintaining grammatical flow. Example: "{{#if name}}Hey {{name}}, {{/if}}your order is ready" becomes just "your order is ready" when name is missing.',
          pattern: MacroPattern.CONDITIONAL
        },
        {
          label: 'Show alternative text for everyone',
          value: 'alternative',
          description: 'Display personalized content when available, friendly generic content otherwise. Example: "{{#if name}}Welcome back, {{name}}{{else}}Welcome{{/if}}!" ensures everyone gets a greeting.',
          pattern: MacroPattern.CONDITIONAL_ELSE
        }
      ]
    }
  }

  const handleOptionClick = (option: typeof questions.q1.options[0]) => {
    if (option.pattern) {
      // Final answer - select pattern
      if (option.pattern === MacroPattern.ADVANCED) {
        setShowAdvancedExamples(true)
      } else {
        onSelectPattern(option.pattern)
      }
    } else if (option.nextQuestion) {
      // Move to next question
      setCurrentQuestionId(option.nextQuestion)
    }
  }

  const handleBack = () => {
    if (currentQuestionId === 'q2') {
      setCurrentQuestionId('q1')
    }
    setShowAdvancedExamples(false)
  }

  const handleReset = () => {
    setCurrentQuestionId('q1')
    setShowAdvancedExamples(false)
  }

  const currentQuestion = questions[currentQuestionId]

  const patternExamples = {
    [MacroPattern.SIMPLE]: {
      title: 'Simple Replacement',
      syntax: '{{key}}',
      example: 'Your order {{confirmationref}} is ready.',
      result: 'Your order ORD-123 is ready.',
      description: 'Direct replacement. Use when data is always present.'
    },
    [MacroPattern.FALLBACK]: {
      title: 'Fallback Value',
      syntax: "{{key|'default'}}",
      example: "Hello {{firstname|'there'}}!",
      result: 'Hello there! (when firstname is missing)',
      description: 'Shows default value when data is missing.'
    },
    [MacroPattern.CONDITIONAL]: {
      title: 'Conditional Block',
      syntax: '{{#if key}}...{{/if}}',
      example: "{{#if firstname}}Hey {{firstname}}, {{/if}}your order is confirmed.",
      result: 'your order is confirmed. (when firstname is missing)',
      description: 'Hides entire phrase when data is missing.'
    },
    [MacroPattern.CONDITIONAL_ELSE]: {
      title: 'Conditional with Alternative',
      syntax: '{{#if key}}...{{else}}...{{/if}}',
      example: "{{#if firstname}}Hey {{firstname}}, {{else}}Hi there, {{/if}}welcome!",
      result: 'Hi there, welcome! (when firstname is missing)',
      description: 'Shows different content based on data availability.'
    }
  }

  const advancedExamples = [
    {
      title: 'Conditional + Fallback',
      code: "{{#if firstname}}Hey {{firstname}}, {{/if}}your order {{confirmationref|'N/A'}} is confirmed.",
      scenarios: [
        { data: 'firstname: "John", confirmationref: "#1234"', result: 'Hey John, your order #1234 is confirmed.' },
        { data: 'No firstname, no confirmationref', result: 'your order N/A is confirmed.' }
      ]
    },
    {
      title: 'Multiple Conditionals',
      code: "{{#if firstname}}Welcome back, {{firstname}}!{{else}}Welcome!{{/if}} {{#if loyaltypoints}}You have {{loyaltypoints}} points.{{/if}}",
      scenarios: [
        { data: 'firstname: "Sarah", loyaltypoints: "500"', result: 'Welcome back, Sarah! You have 500 points.' },
        { data: 'Only firstname', result: 'Welcome back, Sarah!' },
        { data: 'No data', result: 'Welcome!' }
      ]
    },
    {
      title: 'Nested + Fallback',
      code: "{{#if firstname}}Hey {{firstname}}, {{/if}}your {{ordertype|'order'}} {{#if amount}}for ${{amount}}{{/if}} is ready.",
      scenarios: [
        { data: 'firstname: "Alex", ordertype: "purchase", amount: "75.50"', result: 'Hey Alex, your purchase for $75.50 is ready.' },
        { data: 'ordertype missing, amount: "20"', result: 'your order for $20 is ready.' },
        { data: 'Only ordertype: "subscription"', result: 'your subscription is ready.' }
      ]
    },
    {
      title: 'Complex Grammar Handling',
      code: "{{#if firstname}}{{firstname}}'s{{else}}Your{{/if}} order {{confirmationref|'is being processed'}}{{#if amount}}, totaling ${{amount}},{{/if}} is confirmed.",
      scenarios: [
        { data: 'firstname: "Jon", confirmationref: "ORD-123", amount: "45.50"', result: "Jon's order ORD-123, totaling $45.50, is confirmed." },
        { data: 'No name, confirmationref: "ORD-456"', result: 'Your order ORD-456 is confirmed.' },
        { data: 'No data', result: 'Your order is being processed is confirmed.' }
      ]
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#FE4215] to-[#FF6B35] rounded-full mb-4">
          <HelpCircle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#1A2B49] dark:text-white mb-2">
          {showAdvancedExamples
            ? 'Advanced Macro Patterns'
            : selectedPattern && selectedPattern !== MacroPattern.ADVANCED
              ? 'Recommended Macro Pattern'
              : 'Choose Your Macro Pattern'}
        </h2>
        <p className="text-[#6E6D7A] dark:text-gray-400 max-w-2xl mx-auto">
          {showAdvancedExamples
            ? 'Learn how to combine multiple patterns for sophisticated customer experiences'
            : selectedPattern && selectedPattern !== MacroPattern.ADVANCED
              ? 'Based on your answers, this is the recommended macro pattern for your use case. Review it below, then continue to configure it.'
              : 'Macros dynamically insert data into your messages. Answer the questions below to get the best recommended pattern for your use case.'}
        </p>
      </div>

      {/* Progress Indicator (only show when in questionnaire) */}
      {!selectedPattern && !showAdvancedExamples && (
        <div className="flex items-center justify-center space-x-2">
          {['q1', 'q2'].map((qId) => (
            <div 
              key={qId}
              className={`h-2 rounded-full transition-all ${
                qId === currentQuestionId 
                  ? 'w-8 bg-[#FE4215]' 
                  : 'w-2 bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* Advanced Examples View */}
        {showAdvancedExamples && !selectedPattern && (
          <motion.div
            key="advanced-examples"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-2">
                    Understanding Pattern Building Blocks:
                  </p>
                  <p className="text-xs text-purple-800 dark:text-purple-200 mb-3">
                    Advanced patterns combine these components like LEGO blocks to handle any scenario.
                  </p>
                  <div className="grid md:grid-cols-2 gap-3 text-xs text-purple-800 dark:text-purple-200">
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500">•</span>
                      <div>
                        <code className="bg-purple-100 dark:bg-purple-900/40 px-1.5 py-0.5 rounded">{`{{key}}`}</code>
                        <span className="ml-1">Direct data insertion</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500">•</span>
                      <div>
                        <code className="bg-purple-100 dark:bg-purple-900/40 px-1.5 py-0.5 rounded">{`{{key|'default'}}`}</code>
                        <span className="ml-1">With safety fallback</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500">•</span>
                      <div>
                        <code className="bg-purple-100 dark:bg-purple-900/40 px-1.5 py-0.5 rounded">{`{{#if key}}...{{/if}}`}</code>
                        <span className="ml-1">Show/hide sections</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500">•</span>
                      <div>
                        <code className="bg-purple-100 dark:bg-purple-900/40 px-1.5 py-0.5 rounded">{`{{#if}}...{{else}}...{{/if}}`}</code>
                        <span className="ml-1">Branching logic</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
              <p className="text-sm text-indigo-900 dark:text-indigo-100">
                <strong>💡 Pro Tip:</strong> Start with simpler patterns and combine them as needed. Each example below shows real-world scenarios and how multiple patterns work together.
              </p>
            </div>

            {/* Advanced Examples */}
            <div className="space-y-4">
              {advancedExamples.map((example, idx) => (
                <div key={idx} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Code className="w-5 h-5 text-[#FE4215] flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-[#1A2B49] dark:text-white mb-2">{example.title}</h4>
                      <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 mb-3">
                        <p className="text-xs font-mono text-gray-700 dark:text-gray-300 break-all">{example.code}</p>
                      </div>
                      <div className="space-y-2">
                        {example.scenarios.map((scenario, sIdx) => (
                          <div key={sIdx} className="text-sm">
                            <p className="text-gray-600 dark:text-gray-400 text-xs mb-0.5">
                              <span className="font-semibold">With:</span> {scenario.data}
                            </p>
                            <p className="text-[#1A2B49] dark:text-white italic pl-2 border-l-2 border-[#FE4215]">
                              → "{scenario.result}"
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-2 text-sm text-[#6E6D7A] hover:text-[#FE4215] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                ← Back to options
              </button>
              <button
                onClick={() => onSelectPattern(MacroPattern.ADVANCED)}
                className="px-6 py-3 bg-[#FE4215] hover:bg-[#E5350A] text-white font-medium rounded-lg transition-colors shadow-sm hover:shadow-md"
              >
                Build Advanced Pattern →
              </button>
            </div>
          </motion.div>
        )}

        {/* Question Flow */}
        {!selectedPattern && !showAdvancedExamples && currentQuestion && (
          <motion.div
            key={currentQuestionId}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
        {/* Question Flow */}
        {!selectedPattern && !showAdvancedExamples && currentQuestion && (
          <motion.div
            key={currentQuestionId}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-lg font-semibold text-[#1A2B49] dark:text-white mb-1">
                    {currentQuestion.question}
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {currentQuestionId === 'q1' ? 'Each pattern solves different data scenarios. Choose the one that matches your needs.' : 'Both options keep your messages professional—it\'s about the experience you want to create.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {currentQuestion.options.map((option) => (
                <motion.button
                  key={option.value}
                  onClick={() => handleOptionClick(option)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full p-4 text-left border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-[#FE4215] hover:bg-[#FE4215]/5 dark:hover:bg-[#FE4215]/10 transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-[#1A2B49] dark:text-white mb-1 group-hover:text-[#FE4215]">
                        {option.label}
                      </p>
                      <p className="text-sm text-[#6E6D7A] dark:text-gray-400">
                        {option.description}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#FE4215] flex-shrink-0 ml-2" />
                  </div>
                </motion.button>
              ))}
            </div>

            {currentQuestionId !== 'q1' && (
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-2 text-sm text-[#6E6D7A] hover:text-[#FE4215] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                ← Previous question
              </button>
            )}

            {currentQuestionId === 'q1' && onBack && (
              <button
                onClick={onBack}
                className="inline-flex items-center gap-2 text-sm text-[#6E6D7A] hover:text-[#FE4215] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                ← Back
              </button>
            )}
          </motion.div>
        )}

        {/* Debug/Fallback when no current question */}
        {!selectedPattern && !showAdvancedExamples && !currentQuestion && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                No questions available. Current ID: {currentQuestionId}
              </p>
              <button
                onClick={() => {
                  setCurrentQuestionId('q1')
                  setShowAdvancedExamples(false)
                }}
                className="mt-2 px-3 py-1 bg-red-100 hover:bg-red-200 dark:bg-red-800 dark:hover:bg-red-700 text-red-800 dark:text-red-200 rounded text-sm transition-colors"
              >
                Reset to first question
              </button>
            </div>
          </motion.div>
        )}
          </motion.div>
        )}

        {/* Selected Pattern Display */}
        {selectedPattern && selectedPattern !== MacroPattern.ADVANCED && (
          <motion.div
            key="selected-pattern"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4"
          >
            <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-500 dark:border-green-700 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-900 dark:text-green-100 mb-1">
                    {patternExamples[selectedPattern].title}
                  </h3>
                  <p className="text-sm text-green-800 dark:text-green-200 mb-4">
                    {patternExamples[selectedPattern].description}
                  </p>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-200 dark:border-green-800">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Syntax:</p>
                    <code className="text-sm font-mono text-[#1A2B49] dark:text-white block mb-3">
                      {patternExamples[selectedPattern].syntax}
                    </code>
                    
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Example:</p>
                    <code className="text-sm font-mono text-[#6E6D7A] dark:text-gray-300 block mb-2">
                      {patternExamples[selectedPattern].example}
                    </code>
                    
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Result:</p>
                    <p className="text-sm italic text-[#6E6D7A] dark:text-gray-300">
                      {patternExamples[selectedPattern].result}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 text-sm text-[#6E6D7A] hover:text-[#FE4215] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Start over with different answers
              </button>
              <button
                onClick={() => onSelectPattern(null as any)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
              >
                Change Selection
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default PatternSelectionStep
